import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha, useTheme } from '@mui/material/styles';
import { axiosInstance } from '../../services/axiosInstance';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import { GRADES, Grade, GrantRow } from '../../brand/menuCatalog';
import { invalidatePoolMonitorGrantsCache } from '../../brand/poolMonitorPolicy';

type PoolRow = { id: number; code: string; name: string };
type PoolGrants = Record<string, GrantRow>;

const GRADE_LABEL: Record<Grade, string> = {
  super: '최고관리자',
  manager: '일반관리자',
  facility: '시설',
};

const COLS = 'minmax(0, 1fr) 104px 104px 88px';

function defaultRow(): GrantRow {
  return { super: true, manager: true, facility: true };
}

function normalizeRow(row: Partial<GrantRow> | undefined): GrantRow {
  const next: GrantRow = {
    super: row?.super !== false,
    manager: row?.manager !== false,
    facility: row?.facility !== false,
  };
  if (next.facility) {
    next.manager = true;
    next.super = true;
  } else if (next.manager) {
    next.super = true;
  }
  return next;
}

/** 메뉴 허용과 동일: 하위 등급 켜면 상위도, 상위 끄면 하위도 */
function applyToggle(grants: PoolGrants, poolKey: string, grade: Grade, checked: boolean): PoolGrants {
  const next: PoolGrants = { ...grants };
  const row = { ...normalizeRow(next[poolKey]) };
  const idx = GRADES.indexOf(grade);
  if (checked) {
    for (let i = 0; i <= idx; i += 1) row[GRADES[i]] = true;
  } else {
    for (let i = idx; i < GRADES.length; i += 1) row[GRADES[i]] = false;
  }
  next[poolKey] = normalizeRow(row);
  return next;
}

function gradeColor(
  theme: { palette: { primary: { main: string }; success: { main: string }; warning: { main: string } } },
  grade: Grade
): string {
  if (grade === 'super') return theme.palette.primary.main;
  if (grade === 'manager') return theme.palette.success.main;
  return theme.palette.warning.main;
}

/**
 * brand-manager: 시설을 나열하고 등급별 관제 보기 권한 체크.
 * 체크 안 된 등급은 로그인 관제에서 해당 시설이 나오지 않음.
 */
const BrandManagerPoolAccessSection: React.FC = () => {
  const theme = useTheme();
  /** 기본 접힘 — 제목만 보이게 */
  const [open, setOpen] = useState(false);
  const [pools, setPools] = useState<PoolRow[]>([]);
  const [draft, setDraft] = useState<PoolGrants>({});
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axiosInstance.get('/api/brand-menu/pool-monitor-policy');
      const list = Array.isArray(data?.pools) ? data.pools : [];
      const rows: PoolRow[] = list
        .map((p: any) => ({
          id: Number(p?.id),
          code: String(p?.code || ''),
          name: String(p?.name || ''),
        }))
        .filter((p: PoolRow) => Number.isFinite(p.id) && p.id > 0);
      const grantsIn = data?.grants && typeof data.grants === 'object' ? data.grants : {};
      const next: PoolGrants = {};
      for (const p of rows) {
        const key = String(p.id);
        next[key] = normalizeRow(grantsIn[key]);
      }
      setPools(rows);
      setDraft(next);
      setLoadedOnce(true);
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '시설 관제 권한 조회 실패'));
      setPools([]);
      setDraft({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || loadedOnce) return;
    void load();
  }, [open, loadedOnce, load]);

  const onToggle = (poolId: number, grade: Grade, checked: boolean) => {
    setDraft((prev) => applyToggle(prev, String(poolId), grade, checked));
    setMessage('');
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const { data } = await axiosInstance.put('/api/brand-menu/pool-monitor-policy', {
        grants: draft,
      });
      if (data?.grants && typeof data.grants === 'object') {
        const next: PoolGrants = {};
        for (const p of pools) {
          const key = String(p.id);
          next[key] = normalizeRow(data.grants[key]);
        }
        setDraft(next);
      }
      invalidatePoolMonitorGrantsCache();
      setMessage('시설 관제 보기 권한 저장 완료. 체크 안 된 등급은 관제에 시설이 표시되지 않음.');
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '시설 관제 권한 저장 실패'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ mb: 3, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>개별 시설 관제 보기 권한</Typography>
          {!open ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
              클릭하여 시설별 등급 권한 설정
            </Typography>
          ) : null}
        </Box>
        <IconButton
          size="small"
          aria-label={open ? '접기' : '펼치기'}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit={false}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5, lineHeight: 1.6 }}>
            시설을 나열하고 등급별로 관제 보기 허용. 메뉴 허용과 같은 방식.
            체크하지 않은 등급으로 로그인한 관리자·시설 화면에는 해당 시설 관제가 나오지 않음.
          </Typography>

          {loading ? (
            <CircularProgress size={22} />
          ) : pools.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              등록된 시설 없음
            </Typography>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                maxHeight: 420,
                overflowY: 'auto',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  bgcolor: 'action.hover',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>시설</Typography>
                {GRADES.map((grade) => (
                  <Typography
                    key={grade}
                    sx={{
                      textAlign: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                      color: gradeColor(theme, grade),
                    }}
                  >
                    {GRADE_LABEL[grade]}
                  </Typography>
                ))}
              </Box>
              {pools.map((p) => {
                const key = String(p.id);
                const row = normalizeRow(draft[key]);
                const label = [p.code, p.name].filter(Boolean).join(' — ') || `시설 #${p.id}`;
                return (
                  <Box
                    key={p.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: COLS,
                      alignItems: 'center',
                      minHeight: 48,
                      px: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 500, pr: 1 }}>{label}</Typography>
                    {GRADES.map((grade) => (
                      <Box key={grade} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                          checked={Boolean(row[grade])}
                          onChange={(e) => onToggle(p.id, grade, e.target.checked)}
                          inputProps={{ 'aria-label': `${label} ${GRADE_LABEL[grade]}` }}
                          sx={{
                            color: alpha(gradeColor(theme, grade), 0.7),
                            '&.Mui-checked': { color: gradeColor(theme, grade) },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => void save()}
              disabled={saving || loading || pools.length === 0}
            >
              {saving ? '저장 중' : '시설 관제 권한 저장'}
            </Button>
            <Button variant="outlined" onClick={() => void load()} disabled={saving || loading}>
              다시 불러오기
            </Button>
          </Box>
          {message ? (
            <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setMessage('')}>
              {message}
            </Alert>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError('')}>
              {error}
            </Alert>
          ) : null}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default BrandManagerPoolAccessSection;
